import type { Table } from "common/data/table";
import { planarGraphVertexStripToPolyLine2 } from "geom/planarGraph";
import type { Point } from "geom/polyclip/dcel";
import { pointInPolygon } from "geom/polyclip/pointInPolygon";
import { polyLine2ToPolyClipPolygonLoop } from "geom/polyclip/polyClip";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";

/**
 * Returns the planar graph faces that contain the given point.
 * @param point - The query point.
 * @param planarGraphs - The planar graphs to use.
 * @param faceIdsByPlanarGraphId - The face ids belonging to each planar graph.
 * @param faceIndexByFaceId - The index of a face id in a planar graph.
 * @param planarGraphIds - The specific planar graph ids to check. If not provided, all graphs will be used.
 */
export function faceIdsFromPlanarGraphsByPoint(
  point: Point | null,
  planarGraphs: Table<PolygonPlanarGraph>,
  faceIdsByPlanarGraphId: Table<string[]>,
  faceIndexByFaceId: Table<number>,
  planarGraphIds?: string[],
): string[] {
  const faceIds: string[] = [];

  if (!point) {
    return faceIds;
  }

  for (const planarGraphId of planarGraphIds ?? Object.keys(faceIdsByPlanarGraphId)) {
    const faceIdsInPlanarGraph = faceIdsByPlanarGraphId[planarGraphId];
    const planarGraph = planarGraphs[planarGraphId];

    const planarGraphFaceToIndex = faceToIndexLookup(faceIdsInPlanarGraph, planarGraph, faceIndexByFaceId);

    for (let i = 0; i < planarGraphFaceToIndex.length; i++) {
      const polygonIndex = planarGraph.planarGraphFaceToPolygon(i);

      const faceOutline = planarGraphVertexStripToPolyLine2(
        planarGraph.planarGraph,
        planarGraph.planarGraph.faceVertices[i],
        true,
      );

      const polygonLoop = polyLine2ToPolyClipPolygonLoop(faceOutline, false, false);

      const isPointInPolygon = pointInPolygon(point, polygonLoop);

      if (isPointInPolygon) {
        const id = faceIdsInPlanarGraph[polygonIndex];

        if (id) {
          faceIds.push(id);
        }
      }
    }
  }

  return faceIds;
}

function faceToIndexLookup(
  faceIds: string[],
  planarGraph: PolygonPlanarGraph,
  planarGraphIdsByFaceId: Table<number>,
): number[] {
  const { polygonToPlanarGraph } = planarGraph;

  const planarGraphFaceToIndex: number[] = [];

  for (let i = 0, l = faceIds.length; i < l; i++) {
    const faceIndex = faceIds[i];
    const planarGraphId = planarGraphIdsByFaceId[faceIndex];
    const edge = polygonToPlanarGraph[planarGraphId][0][0];
    planarGraphFaceToIndex[edge.index] = planarGraphId;
  }

  return planarGraphFaceToIndex;
}
